import { ButtonIcon, styled, SyncIcon, Tooltip } from 'canoe';
import debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useState } from 'react';

import { ValueBox, ValueBoxGroupOptions } from '@candu-ai/craft/ui';
import { fontWeightOptions } from '@candu-ai/styleguide/ui';
import { DropdownBox } from 'craft/containers/Toolbox/components';
import { getCssResetStyleMessage, StylesHierarchy } from 'craft/hooks';
import { useViewportBreakpoint } from 'craft/state';

import { useStyleguideFonts } from '../useStyleguideFonts';

const StyledDropdownBox = styled(DropdownBox)`
  min-width: 95px;
  margin-bottom: 0;
`;

const globalValues = ['inherit', 'initial', 'unset'];

interface Props {
  cssProps: Record<string, unknown>;
  setCss: (e: Record<string, unknown>) => void;
  stylesHierarchy: StylesHierarchy;
  isInGroup?: ValueBoxGroupOptions;
  variant?: string;
}

export const FontWeight = ({ cssProps, setCss, isInGroup, stylesHierarchy, variant }: Props) => {
  const { fontFamily, fontWeight } = cssProps;
  const viewport = useViewportBreakpoint();
  const styleguideFonts = useStyleguideFonts();

  const defaultValue = stylesHierarchy?.fontWeight?.fallbackValue || 'initial';
  const [inputValue, setInputValue] = useState<string>((fontWeight || defaultValue) as string);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setInputValue((fontWeight || defaultValue) as string);
  }, [viewport, variant]);

  const match = useMemo(() => {
    return styleguideFonts.find((font) => font.family === fontFamily);
  }, [fontFamily]);

  const availableOptions = match?.variants
    ? fontWeightOptions.filter((option) => (match.variants as string[]).includes(option))
    : fontWeightOptions;

  const items = [...availableOptions, ...globalValues].map((v) => ({ name: v, value: v }));

  const filteredItems = useMemo(() => {
    if (!searchValue) {
      return items;
    }

    return items.filter((item) => {
      return item.name.toLowerCase().includes(searchValue?.toLowerCase());
    });
  }, [searchValue, items]);

  const updateSearchValue = debounce((search: string) => {
    setSearchValue(search);
  }, 300);

  return (
    <ValueBox label="Font Weight" isInGroup={isInGroup} showOverflow>
      <StyledDropdownBox
        boxCss={{
          borderBottomRightRadius: 0,
          borderBottomLeftRadius: 0,
          borderTopRightRadius: 0,
          borderTopLeftRadius: 0,
        }}
        isSearchable
        onSearchChange={updateSearchValue}
        searchInputProps={{
          placeholder: `Search weight`,
        }}
        value={inputValue}
        options={filteredItems}
        onChange={(selectedValue: string) => {
          if (selectedValue) {
            setInputValue(selectedValue);
            setCss({ fontWeight: selectedValue });
          }
        }}
        suffix={
          !!fontWeight && inputValue !== defaultValue ? (
            <Tooltip
              placement="top"
              content={getCssResetStyleMessage(stylesHierarchy?.fontWeight?.fallbackStyle)}
            >
              <ButtonIcon
                icon={SyncIcon}
                color="contrast"
                size="xsmall"
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  setInputValue(defaultValue as string);
                  setCss({ fontWeight: undefined });
                }}
              />
            </Tooltip>
          ) : undefined
        }
      />
    </ValueBox>
  );
};
